/**
 * Akış scene: board + conveyor ring + tray + shooter queues + the guess ("Tahmin et") second layer.
 * All rules live in ./core (pure TS); this file only draws and animates the results of
 * applyAction(). The simulation is always one action ahead of the animation, so every callback
 * reads `this.state` for rules and uses `shown*` fields for what is currently on screen.
 * Player-facing text: ./i18n.ts.
 */
import Phaser from "phaser";
import { decodePixels, type Picture } from "@enjoyment/core";
import { exposeBotHook, loadSave, writeSave } from "@kit/boot";
import { afterSkip, afterWin, mapBack, mapText, openMap, starLine } from "@kit/map";
import { recordSkip, recordWin } from "@kit/progress";
import { openGuess, paintPicture } from "@kit/guess";
import { currentLang } from "@kit/lang";
import { track } from "@kit/telemetry";
import { backdrop, button, centerCamera, confetti, floatText, FONT, share, text, TEXT_DIM, topBar, W } from "@kit/ui";
import { loadPictures } from "@shared/art/pictures";
import { sfx, unlockAudio } from "@shared/audio";
import { Rng } from "@enjoyment/core";
import { chooseAction, GREEDY } from "./core/bot";
import { generateLevel } from "./core/generator";
import { cellPixels } from "./core/picture";
import { applyAction, clearedPct, createGame, evalLaunch, isStuck, shooterAt, starsFor } from "./core/sim";
import type { Action, ActionResult, Shooter, Shot, State } from "./core/types";
import { t } from "./i18n";

const ID = "akis";
export const PALETTE = [0xff595e, 0xffca3a, 0x8ac926, 0x3a9bf0, 0xb07cf0, 0xff924c];
const PANEL_DARK = 0x12152b;
const SLOT_BG = 0x1c2040;
const TRACK = 0x2a3058;
const OUTER = 672;
const BAND = 44;
const QUEUE_H = 272;
const TRAY_H = 100;
const SLOT_W = 86;
const SLOT_GAP = 6;
const SCALE_RING = 0.46;
const SCALE_TRAY = 0.74;
const PIC_K = 16; // canvas pixels per picture pixel

interface Save {
  level: number;
  fails: number;
  best: Record<string, number>;
}

const shade = (c: number, f: number) => {
  const r = Math.min(255, Math.round(((c >> 16) & 255) * f));
  const g = Math.min(255, Math.round(((c >> 8) & 255) * f));
  const b = Math.min(255, Math.round((c & 255) * f));
  return (r << 16) | (g << 8) | b;
};

/** A chunky shooter. `body` rotates (barrel direction on the conveyor); the ammo label never does. */
class Cannon {
  readonly root: Phaser.GameObjects.Container;
  readonly body: Phaser.GameObjects.Container;
  readonly label: Phaser.GameObjects.Text;
  ammo: number;
  constructor(
    scene: Phaser.Scene,
    readonly id: number,
    readonly color: number,
    ammo: number,
  ) {
    this.ammo = ammo;
    const c = PALETTE[color % PALETTE.length]!;
    const g = scene.add.graphics();
    // feet, barrel, body, gloss
    g.fillStyle(shade(c, 0.5), 1).fillRoundedRect(-44, 28, 22, 18, 6).fillRoundedRect(22, 28, 22, 18, 6);
    g.fillStyle(shade(c, 0.62), 1).fillRoundedRect(-14, -52, 28, 38, 7);
    g.fillStyle(0x0d1024, 0.55).fillRoundedRect(-9, -52, 18, 9, 4);
    g.fillStyle(shade(c, 0.7), 1).fillRoundedRect(-45, -30, 90, 70, 18);
    g.fillStyle(c, 1).fillRoundedRect(-45, -34, 90, 68, 18);
    g.fillStyle(0xffffff, 0.22).fillRoundedRect(-36, -28, 72, 14, 7);
    this.body = scene.add.container(0, 0, [g]);
    this.label = scene.add
      .text(0, 4, String(ammo), { fontFamily: FONT, fontSize: "42px", color: "#ffffff", fontStyle: "bold", stroke: shade(c, 0.4).toString(16).padStart(6, "0").replace(/^/, "#"), strokeThickness: 6 })
      .setOrigin(0.5);
    this.root = scene.add.container(0, 0, [this.body, this.label]);
  }
  setAmmo(n: number): void {
    this.ammo = n;
    this.label.setText(String(n));
  }
}

interface Layout {
  size: number;
  cell: number;
  outerX: number;
  outerY: number;
  coverX: number;
  coverY: number;
  coverS: number;
  trayTop: number;
  trayX0: number;
  queueTop: number;
  cols: number;
  colW: number;
  colX: number[];
}

export class AkisScene extends Phaser.Scene {
  private save!: Save;
  private startAt = 0;
  private wantLevel = 0;
  private pool: Picture[] = [];
  private picture: Picture | null = null;
  private state!: State;
  private L!: Layout;
  private blocks: (Phaser.GameObjects.Image | null)[] = [];
  private cannons = new Map<number, Cannon>();
  private flyingId = -1;
  private busy = true;
  private ended = false;
  private shownRemaining = 0;
  private hud!: { value: Phaser.GameObjects.Text };
  private picCtx: CanvasRenderingContext2D | null = null;
  private picTex: Phaser.Textures.CanvasTexture | null = null;
  private cellPx: number[][] = [];
  private picPixels: number[] = [];
  private traySlots!: Phaser.GameObjects.Graphics;
  private trayLocks!: Phaser.GameObjects.Graphics;
  private guessBtn?: Phaser.GameObjects.Container;
  private skipBtn?: Phaser.GameObjects.Text;
  private endCard?: Phaser.GameObjects.Container;
  private speed = 1;
  private botRng = new Rng(7);
  private auto = false;

  constructor() {
    super("akis");
  }

  init(data?: { level?: number }): void {
    this.wantLevel = data?.level ?? 0;
  }

  create(): void {
    centerCamera(this);
    backdrop(this, { light: 0x2ec4b6 });
    this.save = loadSave<Save>(ID, { level: 1, fails: 0, best: {} });
    const q = new URLSearchParams(location.search);
    const bot = q.has("bot");
    if (bot) this.speed = 0.35;
    if (!this.wantLevel) this.wantLevel = bot && Number(q.get("level")) > 0 ? Number(q.get("level")) : this.save.level;
    this.busy = true;
    this.ended = false;
    this.cannons.clear();
    this.flyingId = -1;
    this.add.text(W / 2, this.scale.height / 2, t("loading"), { fontFamily: FONT, fontSize: "30px", color: TEXT_DIM }).setOrigin(0.5).setName("loading");
    this.input.once("pointerdown", () => unlockAudio());
    const go = (pool: Picture[]) => {
      if (!this.sys.isActive()) return;
      this.pool = pool;
      this.time.delayedCall(30, () => this.begin());
    };
    loadPictures().then((p) => go(p.pictures), () => go([]));
    exposeBotHook({
      state: () => this.publicState(),
      autoplay: () => {
        void this.autoplay();
      },
      step: () => {
        const a = this.state && chooseAction(this.state, this.botRng, GREEDY);
        if (a) void this.perform(a);
      },
      lose: () => {
        void this.autolose();
      },
    });
  }

  // ------------------------------------------------------------------ level setup

  private begin(): void {
    this.children.getByName("loading")?.destroy();
    const n = this.wantLevel;
    const level = generateLevel(n, this.pool);
    this.picture = this.pool.find((p) => p.id === level.pictureId) ?? null;
    this.state = createGame(level, this.picture);
    this.shownRemaining = this.state.remaining;
    this.startAt = Date.now();
    this.buildLayout();
    this.buildStatic();
    this.buildBoard();
    this.sync();
    this.drawTray();
    this.busy = false;
    track("level_start", { level: n, size: level.size, colors: level.colors, difficulty: level.difficulty ?? "" });
    this.updateGuessButton();
  }

  private buildLayout(): void {
    const H = this.scale.height;
    const level = this.state.level;
    const size = level.size;
    // Fixed stack for a 720x1280 design; taller screens spread the surplus around the board.
    const extra = Math.max(0, H - 1280);
    const outerY = 184 + extra * 0.4;
    const trayTop = outerY + OUTER + 24 + extra * 0.25;
    const queueTop = trayTop + TRAY_H + 14 + extra * 0.2;
    const outerX = (W - OUTER) / 2;
    const coverS = OUTER - 2 * (BAND + 8);
    const cols = level.queues.length;
    const colW = Math.min(150, 664 / cols);
    const x0 = (W - cols * colW) / 2;
    this.L = {
      size,
      cell: coverS / size,
      outerX,
      outerY,
      coverX: outerX + BAND + 8,
      coverY: outerY + BAND + 8,
      coverS,
      trayTop,
      trayX0: 24,
      queueTop,
      cols,
      colW,
      colX: Array.from({ length: cols }, (_, i) => x0 + colW * (i + 0.5)),
    };
  }

  private buildStatic(): void {
    const { L } = this;
    const level = this.state.level;
    this.hud = topBar(this, t("topTitle", { n: level.n }), t("left"), mapBack(this, "akis"));
    this.hud.value.setText(String(this.shownRemaining));
    if (level.n === 1) this.add.text(W / 2, 162, t("hint1"), { fontFamily: FONT, fontSize: "18px", color: TEXT_DIM }).setOrigin(0.5);
    // conveyor track
    const track = this.add.graphics().setDepth(1);
    track.fillStyle(TRACK, 1).fillRoundedRect(L.outerX, L.outerY, OUTER, OUTER, 44);
    track.fillStyle(PANEL_DARK, 1).fillRoundedRect(L.coverX - 8, L.coverY - 8, L.coverS + 16, L.coverS + 16, 16);
    track.lineStyle(3, 0x3a4170, 1).strokeRoundedRect(L.outerX, L.outerY, OUTER, OUTER, 44);
    // direction chevrons (clockwise)
    const total = L.size * 4;
    const chev = this.add.graphics().setDepth(1).fillStyle(0x4a5390, 0.9);
    for (let u = 1.5; u < total; u += Math.max(2, L.size / 2.5)) {
      const a = this.ringXY(u);
      const b = this.ringXY(u + 0.25);
      const ang = Math.atan2(b.y - a.y, b.x - a.x);
      const p = (r: number, off: number) => ({ x: a.x + Math.cos(ang + off) * r, y: a.y + Math.sin(ang + off) * r });
      const t0 = p(8, 0);
      const t1 = p(7, 2.5);
      const t2 = p(7, -2.5);
      chev.fillTriangle(t0.x, t0.y, t1.x, t1.y, t2.x, t2.y);
    }
    // tray
    this.add.text(L.trayX0 + 4, L.trayTop - 14, t("tray"), { fontFamily: FONT, fontSize: "18px", color: TEXT_DIM, fontStyle: "bold" }).setOrigin(0, 0.5);
    this.traySlots = this.add.graphics().setDepth(4);
    this.trayLocks = this.add.graphics().setDepth(60);
    const cap = level.trayCap;
    for (let i = 0; i < cap; i++) {
      const z = this.add.zone(this.slotX(i), L.trayTop + TRAY_H / 2, SLOT_W, TRAY_H).setInteractive();
      z.on("pointerdown", () => {
        unlockAudio();
        void this.perform({ t: "t", i: this.trayIndexAtSlot(i) });
      });
    }
    // queue plates + tap zones
    const plates = this.add.graphics().setDepth(4);
    for (let c = 0; c < L.cols; c++) {
      const x = L.colX[c]!;
      plates.fillStyle(SLOT_BG, 1).fillRoundedRect(x - L.colW / 2 + 5, L.queueTop, L.colW - 10, 128, 18);
      plates.lineStyle(2, 0x3a4170, 1).strokeRoundedRect(x - L.colW / 2 + 5, L.queueTop, L.colW - 10, 128, 18);
      const z = this.add.zone(x, L.queueTop + 64, L.colW, 132).setInteractive();
      z.on("pointerdown", () => {
        unlockAudio();
        void this.perform({ t: "q", i: c });
      });
    }
    // guess button
    if (this.picture) {
      const bx = (L.trayX0 + this.trayWidth() + (W - 24)) / 2;
      this.guessBtn = button(this, bx, L.trayTop + TRAY_H / 2, W - 24 - (L.trayX0 + this.trayWidth()) - 12, 76, t("guess"), 0x2ec4b6, () => this.openGuessCard(), "#0d1024").setDepth(70);
    }
    // free skip after 3 fails (H4)
    if (this.save.fails >= 3) {
      this.skipBtn = this.add
        .text(W / 2 + 10, 70, t("skipTop"), { fontFamily: FONT, fontSize: "30px", color: "#ffca3a", fontStyle: "bold" })
        .setOrigin(0.5)
        .setPadding(20, 14)
        .setInteractive({ useHandCursor: true })
        .on("pointerdown", () => this.skipLevel());
    }
  }

  private buildBoard(): void {
    const { L } = this;
    const level = this.state.level;
    if (this.picture) {
      const key = "akis-pic";
      if (this.textures.exists(key)) this.textures.remove(key);
      const px = this.picture.size * PIC_K;
      this.picTex = this.textures.createCanvas(key, px, px);
      this.picCtx = this.picTex!.getContext();
      this.picPixels = decodePixels(this.picture);
      this.cellPx = cellPixels(L.size, this.picture.size);
      this.add.image(L.coverX, L.coverY, key).setOrigin(0).setDisplaySize(L.coverS, L.coverS).setDepth(2);
      // already-open cells (none at start, but a restored state would paint here)
      for (let i = 0; i < this.state.cover.length; i++) if (this.state.cover[i]! < 0) this.paintCell(i);
      this.picTex!.refresh();
    }
    for (let c = 0; c < PALETTE.length; c++) {
      const key = `akis-blk-${c}`;
      if (this.textures.exists(key)) continue;
      const col = PALETTE[c]!;
      const g = this.add.graphics();
      g.fillStyle(shade(col, 0.68), 1).fillRoundedRect(0, 4, 64, 60, 14);
      g.fillStyle(col, 1).fillRoundedRect(0, 0, 64, 58, 14);
      g.fillStyle(0xffffff, 0.28).fillRoundedRect(7, 6, 50, 11, 5);
      g.generateTexture(key, 64, 64);
      g.destroy();
    }
    this.blocks = new Array(level.size * level.size).fill(null);
    for (let i = 0; i < this.state.cover.length; i++) {
      const v = this.state.cover[i]!;
      if (v < 0) continue;
      const p = this.cellCenter(i);
      this.blocks[i] = this.add.image(p.x, p.y, `akis-blk-${v}`).setDisplaySize(L.cell - 1.5, L.cell - 1.5).setDepth(3);
    }
  }

  // ------------------------------------------------------------------ geometry

  private cellCenter(i: number): { x: number; y: number } {
    const { L } = this;
    return { x: L.coverX + ((i % L.size) + 0.5) * L.cell, y: L.coverY + (Math.floor(i / L.size) + 0.5) * L.cell };
  }

  private slotX(i: number): number {
    return this.L.trayX0 + SLOT_W / 2 + i * (SLOT_W + SLOT_GAP);
  }

  private trayWidth(): number {
    return this.state.level.trayCap * SLOT_W + (this.state.level.trayCap - 1) * SLOT_GAP;
  }

  /** Slot i (screen) -> index into state.tray (shooters occupy the leftmost slots). */
  private trayIndexAtSlot(i: number): number {
    return i;
  }

  /** Conveyor position for continuous step u (u = p is the centre of step p). */
  private ringXY(u: number): { x: number; y: number } {
    const { L } = this;
    const n = L.size;
    const total = n * 4;
    const left = L.outerX + BAND / 2;
    const right = L.outerX + OUTER - BAND / 2;
    const top = L.outerY + BAND / 2;
    const bottom = L.outerY + OUTER - BAND / 2;
    const rowY = (r: number) => L.coverY + (r + 0.5) * L.cell;
    const colX = (c: number) => L.coverX + (c + 0.5) * L.cell;
    const P = (p: number) => {
      const side = Math.floor(p / n);
      const k = p % n;
      if (side === 0) return { x: left, y: rowY(n - 1 - k) };
      if (side === 1) return { x: colX(k), y: top };
      if (side === 2) return { x: right, y: rowY(k) };
      return { x: colX(n - 1 - k), y: bottom };
    };
    const corners = [
      { x: left, y: bottom },
      { x: left, y: top },
      { x: right, y: top },
      { x: right, y: bottom },
    ];
    const uu = Math.max(0, Math.min(total, u));
    let p = Math.floor(uu);
    let f = uu - p;
    if (p >= total) {
      p = total - 1;
      f = 1;
    }
    const a = P(p);
    const b = P((p + 1) % total);
    const lerp = (s: { x: number; y: number }, e: { x: number; y: number }, t: number) => ({ x: s.x + (e.x - s.x) * t, y: s.y + (e.y - s.y) * t });
    if ((p + 1) % n === 0) {
      const c = corners[((p + 1) / n) % 4]!;
      return f < 0.5 ? lerp(a, c, f * 2) : lerp(c, b, (f - 0.5) * 2);
    }
    return lerp(a, b, f);
  }

  private bodyAngle(u: number): number {
    const side = Math.min(3, Math.max(0, Math.floor(u / this.L.size)));
    return (Math.PI / 2) * (side + 1);
  }

  private bl(): { x: number; y: number } {
    return { x: this.L.outerX + BAND / 2, y: this.L.outerY + OUTER - BAND / 2 };
  }

  // ------------------------------------------------------------------ cannons on the shelves

  private queuePos(col: number, j: number): { x: number; y: number; s: number; a: number } {
    const { L } = this;
    const x = L.colX[col]!;
    const front = Math.min(1.1, (L.colW - 16) / 100);
    if (j === 0) return { x, y: L.queueTop + 66, s: front, a: 1 };
    if (j === 1) return { x, y: L.queueTop + 172, s: front * 0.68, a: 0.85 };
    return { x, y: L.queueTop + 236, s: front * 0.5, a: 0.6 };
  }

  private trayPos(i: number): { x: number; y: number; s: number; a: number } {
    return { x: this.slotX(i), y: this.L.trayTop + TRAY_H / 2 + 2, s: SCALE_TRAY, a: 1 };
  }

  /** Make the cannons on screen match the state (queue fronts, tray) — flying one excluded. */
  private sync(): void {
    const want = new Map<number, { sh: Shooter; x: number; y: number; s: number; a: number }>();
    this.state.queues.forEach((q, ci) => q.slice(0, 3).forEach((sh, j) => want.set(sh.id, { sh, ...this.queuePos(ci, j) })));
    this.state.tray.forEach((sh, i) => {
      if (sh.id !== this.flyingId) want.set(sh.id, { sh, ...this.trayPos(i) });
    });
    for (const [id, c] of this.cannons)
      if (!want.has(id)) {
        c.root.destroy();
        this.cannons.delete(id);
      }
    for (const [id, w] of want) {
      let c = this.cannons.get(id);
      const fresh = !c;
      if (!c) {
        c = new Cannon(this, id, w.sh.color, w.sh.ammo);
        c.root.setPosition(w.x, w.y).setScale(w.s * 0.6).setAlpha(0);
        this.cannons.set(id, c);
      }
      if (c.ammo !== w.sh.ammo) c.setAmmo(w.sh.ammo);
      c.root.setDepth(10 + (w.s > 0.9 ? 2 : 0));
      this.tweens.killTweensOf(c.root);
      this.tweens.add({ targets: c.root, x: w.x, y: w.y, scale: w.s, alpha: w.a, duration: fresh ? 220 : 200 * this.speed + 40, ease: "Cubic.Out" });
      this.tweens.killTweensOf(c.body);
      this.tweens.add({ targets: c.body, rotation: Math.round(c.body.rotation / (Math.PI * 2)) * Math.PI * 2, duration: 160 });
    }
    // "+N" behind the third visible shooter
    this.children.getAll().filter((o) => o.name === "more").forEach((o) => o.destroy());
    this.state.queues.forEach((q, ci) => {
      if (q.length > 3) {
        const p = this.queuePos(ci, 2);
        this.add.text(p.x + this.L.colW / 2 - 20, p.y + 2, `+${q.length - 3}`, { fontFamily: FONT, fontSize: "20px", color: TEXT_DIM, fontStyle: "bold" }).setOrigin(1, 0.5).setName("more").setDepth(9);
      }
    });
  }

  private drawTray(): void {
    const g = this.traySlots;
    const lk = this.trayLocks;
    g.clear();
    lk.clear();
    const { L } = this;
    const cap = this.state.level.trayCap;
    const locked = Math.min(cap, this.state.locks);
    for (let i = 0; i < cap; i++) {
      const x = this.slotX(i) - SLOT_W / 2;
      const isLocked = i >= cap - locked;
      g.fillStyle(isLocked ? 0x0f1124 : SLOT_BG, 1).fillRoundedRect(x, L.trayTop, SLOT_W, TRAY_H, 16);
      g.lineStyle(2, isLocked ? 0x6b3b3f : 0x3a4170, 1).strokeRoundedRect(x, L.trayTop, SLOT_W, TRAY_H, 16);
      if (isLocked) {
        const cx = x + SLOT_W - 22;
        const cy = L.trayTop + 24;
        lk.fillStyle(0xffca3a, 1).fillRoundedRect(cx - 13, cy - 4, 26, 20, 5);
        lk.lineStyle(5, 0xffca3a, 1).beginPath().arc(cx, cy - 4, 8, Math.PI, 0, false).strokePath();
        lk.fillStyle(0x12152b, 1).fillCircle(cx, cy + 6, 3);
      }
    }
  }

  // ------------------------------------------------------------------ input -> action

  private async perform(a: Action): Promise<void> {
    if (this.busy || this.ended || !this.state || this.state.status !== "playing") return;
    // an empty queue column / empty tray slot is not an action
    const sh = a.t === "g" ? undefined : shooterAt(this.state, a.t === "q" ? "queue" : "tray", a.i);
    if (a.t !== "g" && !sh) {
      sfx.nope();
      return;
    }
    if (a.t === "t" && !evalLaunch(this.state, "tray", a.i).legal) {
      sfx.nope();
      this.shakeSlot(a.i);
      return;
    }
    this.busy = true;
    const res = applyAction(this.state, a);
    if (!res.ok) {
      sfx.nope();
      this.busy = false;
      return;
    }
    track("move", { kind: a.t === "q" ? "queue" : "tray", shots: res.shots.length, moves: this.state.launches });
    await this.playLaunch(res);
    this.hud.value.setText(String(this.state.remaining));
    this.busy = false;
    const st = this.status();
    if (st === "won") this.finishWin();
    else if (st === "lost") this.finishLose();
    else {
      this.updateGuessButton();
      if (isStuck(this.state) && this.picture) floatText(this, W / 2, this.L.trayTop - 40, t("stuck"), "#2ec4b6", 34);
    }
  }

  private status(): State["status"] {
    return this.state.status;
  }

  private shakeSlot(i: number): void {
    const c = this.cannons.get(this.state.tray[i]?.id ?? -1);
    if (c) this.tweens.add({ targets: c.root, x: c.root.x + 8, duration: 50, yoyo: true, repeat: 2 });
  }

  private tw(cfg: Phaser.Types.Tweens.TweenBuilderConfig): Promise<void> {
    return new Promise((resolve) => {
      this.tweens.add({ ...cfg, duration: (cfg.duration as number) * this.speed, onComplete: () => resolve() });
    });
  }

  private wait(ms: number): Promise<void> {
    return new Promise((resolve) => this.time.delayedCall(ms * this.speed, resolve));
  }

  private place(cn: Cannon, u: number): void {
    const p = this.ringXY(u);
    cn.root.setPosition(p.x, p.y);
    cn.body.rotation = Phaser.Math.Angle.RotateTo(cn.body.rotation, this.bodyAngle(u), 0.3);
  }

  private async runTo(cn: Cannon, from: number, to: number, perStep: number): Promise<void> {
    if (to <= from) return;
    const o = { u: from };
    await this.tw({ targets: o, u: to, duration: Math.max(30, (to - from) * perStep), ease: "Linear", onUpdate: () => this.place(cn, o.u) });
  }

  private async playLaunch(res: ActionResult): Promise<void> {
    const info = res.shooter!;
    const total = this.L.size * 4;
    let cn = this.cannons.get(info.id);
    this.cannons.delete(info.id);
    if (!cn) {
      cn = new Cannon(this, info.id, info.color, info.ammoBefore);
      const p = res.from === "queue" ? this.queuePos(res.index!, 0) : this.trayPos(res.index!);
      cn.root.setPosition(p.x, p.y).setScale(p.s);
    }
    this.flyingId = info.id;
    sfx.special();
    cn.root.setDepth(100);
    this.sync(); // others shift forward
    if (res.releasedLock) this.drawTray();
    // pop onto the ring at its start (bottom-left corner)
    const start = this.bl();
    await this.tw({ targets: cn.root, x: start.x, y: start.y, scale: SCALE_RING, duration: 230, ease: "Cubic.InOut" });
    cn.body.rotation = 0;
    const zeroShot = res.shots.length === 0;
    const perStep = (zeroShot ? 6 : 11.5) * this.speed;
    let u = 0;
    this.place(cn, 0);
    let ammo = info.ammoBefore;
    let i = 0;
    for (const shot of res.shots) {
      await this.runTo(cn, u, shot.step, perStep / this.speed);
      u = shot.step;
      ammo--;
      i++;
      this.fire(cn, shot, i);
      cn.setAmmo(ammo);
      this.shownRemaining--;
      this.hud.value.setText(String(Math.max(0, this.shownRemaining)));
      await this.wait(res.shots.length > 12 ? 32 : 58);
    }
    if (res.shots.length >= 8) {
      this.cameras.main.shake(140, 0.004);
      floatText(this, this.ringXY(u).x, this.L.outerY + OUTER / 2, `x${res.shots.length}`, "#ffffff", 72);
      track("moment", { type: "chain", shots: res.shots.length, level: this.state.level.n });
    }
    if (res.outcome === "parked" || res.outcome === "lost") {
      await this.runTo(cn, u, total - 0.5, perStep / this.speed);
      this.place(cn, total - 0.5);
    }
    if (res.outcome === "emptied") {
      await this.poof(cn);
    } else if (res.outcome === "parked") {
      this.cannons.set(info.id, cn);
      this.flyingId = -1;
      cn.body.rotation = Phaser.Math.Angle.Wrap(cn.body.rotation);
      this.sync();
      sfx.goalTick();
      await this.wait(240);
      this.drawTray();
    } else if (res.outcome === "lost") {
      const tip = this.trayPos(Math.min(this.state.tray.length, this.state.level.trayCap - 1));
      await this.tw({ targets: cn.root, x: tip.x, y: tip.y, scale: SCALE_TRAY, duration: 260, ease: "Cubic.In" });
      this.cameras.main.shake(260, 0.01);
      sfx.nope();
      this.tweens.add({ targets: this.traySlots, alpha: 0.3, duration: 90, yoyo: true, repeat: 3 });
      floatText(this, W / 2, this.L.trayTop - 30, t("trayFull"), "#ff595e", 54);
      await this.wait(420);
      cn.root.destroy();
    }
    this.flyingId = -1;
    this.shownRemaining = this.state.remaining;
  }

  private async poof(cn: Cannon): Promise<void> {
    const { x, y } = cn.root;
    this.burst(x, y, PALETTE[cn.color % PALETTE.length]!, 8, 34);
    await this.tw({ targets: cn.root, scale: 0, alpha: 0, duration: 160, ease: "Back.In" });
    cn.root.destroy();
  }

  private fire(cn: Cannon, shot: Shot, k: number): void {
    const col = PALETTE[shot.color % PALETTE.length]!;
    const from = { x: cn.root.x, y: cn.root.y };
    const to = this.cellCenter(shot.idx);
    const g = this.add.graphics().setDepth(90);
    g.lineStyle(7, col, 1).lineBetween(from.x, from.y, to.x, to.y);
    g.lineStyle(2, 0xffffff, 0.9).lineBetween(from.x, from.y, to.x, to.y);
    this.tweens.add({ targets: g, alpha: 0, duration: 170, onComplete: () => g.destroy() });
    sfx.pop(Math.min(9, 2 + k));
    this.popBlock(shot.idx);
    this.tweens.add({ targets: cn.root, scale: SCALE_RING * 1.25, duration: 40, yoyo: true });
  }

  private popBlock(idx: number): void {
    const img = this.blocks[idx];
    if (!img) return;
    this.blocks[idx] = null;
    const { x, y } = img;
    this.burst(x, y, PALETTE[this.state.level.cover[idx]! % PALETTE.length]!, 5, this.L.cell * 0.9);
    this.tweens.add({ targets: img, scale: img.scale * 1.4, alpha: 0, duration: 130, onComplete: () => img.destroy() });
    if (this.picCtx) {
      this.paintCell(idx);
      this.picTex!.refresh();
    }
  }

  private burst(x: number, y: number, color: number, n: number, reach: number): void {
    for (let i = 0; i < n; i++) {
      const a = (Math.PI * 2 * i) / n + (i % 2) * 0.4;
      const r = this.add.rectangle(x, y, 7, 7, color).setDepth(95).setAngle(i * 37);
      this.tweens.add({ targets: r, x: x + Math.cos(a) * reach, y: y + Math.sin(a) * reach, alpha: 0, scale: 0.3, duration: 260, ease: "Quad.Out", onComplete: () => r.destroy() });
    }
  }

  private paintCell(idx: number): void {
    const ctx = this.picCtx;
    const pic = this.picture;
    if (!ctx || !pic) return;
    for (const p of this.cellPx[idx]!) {
      const x = p % pic.size;
      const y = Math.floor(p / pic.size);
      const col = this.picPixels[p]!;
      ctx.fillStyle = col < 0 ? "#222849" : pic.palette[col]!;
      ctx.fillRect(x * PIC_K, y * PIC_K, PIC_K, PIC_K);
    }
  }

  // ------------------------------------------------------------------ guess (knowledge layer)

  private updateGuessButton(): void {
    if (!this.guessBtn) return;
    this.tweens.killTweensOf(this.guessBtn);
    this.guessBtn.setScale(1);
    if (this.state.status === "playing" && isStuck(this.state))
      this.tweens.add({ targets: this.guessBtn, scale: 1.08, duration: 420, yoyo: true, repeat: -1 });
  }

  private openGuessCard(): void {
    if (this.busy || this.ended || !this.picture || this.state.status !== "playing") return;
    const pic = this.picture;
    const level = this.state.level;
    const px = decodePixels(pic);
    track("guess_open", { level: level.n, pct: clearedPct(this.state) });
    this.busy = true;
    openGuess(this, {
      question: t("question", { pct: clearedPct(this.state) }),
      pool: this.pool,
      lang: currentLang(),
      hint: t("guessHint"),
      paint: (ctx, size) =>
        paintPicture(
          ctx,
          size,
          pic,
          px,
          Array.from(this.state.cover, (v) => v < 0),
          level.size,
          level.size,
        ),
      tried: () => this.state.wrongTexts,
      onSubmit: (txt) => {
        const r = applyAction(this.state, { t: "g", text: txt });
        track("guess", { level: level.n, correct: !!r.correct, pct: clearedPct(this.state) });
        if (r.correct) {
          sfx.win();
          return { correct: true, message: t("correct", { pct: clearedPct(this.state) }), finished: true };
        }
        sfx.nope();
        this.drawTray();
        const full = this.state.locks >= level.trayCap;
        return {
          correct: false,
          message: r.repeated ? t("repeat") : full ? t("wrongFull") : t("wrongLock"),
          finished: false,
        };
      },
      onClose: () => {
        this.busy = false;
        if (this.status() === "won") this.finishWin();
      },
    });
  }

  // ------------------------------------------------------------------ end of level

  private async revealAll(): Promise<void> {
    if (this.picCtx) {
      for (let i = 0; i < this.blocks.length; i++) if (this.blocks[i]) this.paintCell(i);
      this.picTex!.refresh();
    }
    const n = this.L.size;
    this.blocks.forEach((img, i) => {
      if (!img) return;
      const d = ((i % n) + Math.floor(i / n)) * 16;
      this.tweens.add({ targets: img, alpha: 0, scale: img.scale * 0.5, duration: 220, delay: d * this.speed, onComplete: () => img.destroy() });
    });
    this.blocks.fill(null);
    await this.wait(n * 2 * 16 + 260);
  }

  private finishWin(): void {
    if (this.ended) return;
    this.ended = true;
    const s = this.state;
    const n = s.level.n;
    const secs = Math.round((Date.now() - this.startAt) / 1000);
    const by = s.winBy ?? "clear";
    const pct = s.guessPct ?? 100;
    track("level_end", { level: n, result: "won", moves: s.launches, secs, by, pct, wrong: s.wrong });
    if (by === "guess" && pct <= 40) track("moment", { type: "early_guess", pct, level: n });
    this.save.level = Math.max(this.save.level, n + 1);
    this.save.fails = 0;
    this.save.best[String(n)] = Math.max(this.save.best[String(n)] ?? 0, s.score);
    writeSave(ID, this.save);
    const stars = starsFor(s);
    const outcome = recordWin(ID, n, stars);
    this.skipBtn?.destroy();
    this.guessBtn?.destroy();
    void (async () => {
      await this.revealAll();
      sfx.win();
      confetti(this);
      const label = this.picture ? this.picture.label[currentLang()] ?? this.picture.label.tr ?? this.picture.label.en ?? this.picture.id : "";
      const shareText = by === "guess" ? t("shareGuess", { pct }) : t("shareClear", { n });
      const sub =
        by === "guess"
          ? `${starLine(stars)}\n${t("wonGuessSub", { pct })}${label ? ` · ${label}` : ""}${t("score", { score: s.score })}`
          : `${starLine(stars)}\n${t("wonClearSub")}${label ? t("picture", { name: label }) : ""}${t("score", { score: s.score })}`;
      this.showCard(by === "guess" ? t("wonGuess") : t("wonClear"), sub, [
        { label: t("share"), color: 0x1982c4, onClick: () => void this.doShare(shareText) },
        { label: mapText("continue"), color: 0x2ec4b6, onClick: () => this.leave(afterWin(n, outcome)) },
      ]);
    })();
  }

  private finishLose(): void {
    if (this.ended) return;
    this.ended = true;
    const s = this.state;
    const n = s.level.n;
    this.save.fails++;
    writeSave(ID, this.save);
    const secs = Math.round((Date.now() - this.startAt) / 1000);
    track("level_end", { level: n, result: "lost", moves: s.launches, secs, left: s.remaining });
    sfx.lose();
    this.guessBtn?.destroy();
    const buttons = [{ label: t("retry"), color: 0x2ec4b6, onClick: () => this.next(n) }];
    if (this.save.fails >= 3) buttons.push({ label: t("skip"), color: 0xffca3a, onClick: () => this.skipLevel() });
    this.showCard(
      t("lost"),
      this.save.fails >= 3 ? t("lostSkip", { left: s.remaining, fails: this.save.fails }) : t("lostRetry", { left: s.remaining }),
      buttons,
    );
  }

  private skipLevel(): void {
    const n = this.state.level.n;
    track("level_end", { level: n, result: "skip", moves: this.state.launches, secs: Math.round((Date.now() - this.startAt) / 1000) });
    this.save.level = Math.max(this.save.level, n + 1);
    this.save.fails = 0;
    writeSave(ID, this.save);
    this.leave(afterSkip(n, recordSkip(ID, n)));
  }

  private next(level: number): void {
    this.endCard?.destroy();
    this.scene.restart({ level });
  }

  private leave(back: Parameters<typeof openMap>[2]): void {
    this.endCard?.destroy();
    openMap(this, ID, back);
  }

  private async doShare(txt: string): Promise<void> {
    track("share_click", { level: this.state.level.n });
    const ok = await share(txt, "https://akis.enjoyment.app/?utm_source=share", t("title"));
    if (ok) track("share_done", { level: this.state.level.n });
  }

  /** End card in the tray/queue area so the (now revealed) picture stays visible above it. */
  private showCard(title: string, sub: string, buttons: { label: string; color: number; onClick: () => void }[]): void {
    const H = this.scale.height;
    const top = this.L.trayTop - 10;
    const h = H - top - 8;
    const c = this.add.container(0, 0).setDepth(1000);
    const dim = this.add.rectangle(W / 2, top + h / 2, W * 3, h, 0x12152b, 0.97).setInteractive();
    const frame = this.add.graphics();
    frame.lineStyle(3, 0x3a4170, 1).lineBetween(0, top, W, top);
    c.add([dim, frame, text(this, W / 2, top + 66, title, 52), text(this, W / 2, top + 150, sub, 26, "#b8bee6", false).setWordWrapWidth(620).setAlign("center")]);
    const bw = buttons.length > 1 ? 280 : 340;
    const total = buttons.length * bw + (buttons.length - 1) * 24;
    const by = Math.min(H - 80, top + 290);
    buttons.forEach((b, i) => c.add(button(this, W / 2 - total / 2 + bw / 2 + i * (bw + 24), by, bw, 92, b.label, b.color, b.onClick, b.color === 0xffca3a || b.color === 0x2ec4b6 ? "#0d1024" : "#ffffff")));
    c.setAlpha(0);
    this.tweens.add({ targets: c, alpha: 1, duration: 200 });
    this.endCard = c;
  }

  // ------------------------------------------------------------------ bot hook

  private publicState(): unknown {
    const s = this.state;
    if (!s) return { loading: true };
    return {
      level: s.level.n,
      status: s.status,
      winBy: s.winBy,
      remaining: s.remaining,
      launches: s.launches,
      tray: s.tray.length,
      locks: s.locks,
      busy: this.busy,
      difficulty: s.level.difficulty ?? null,
      answer: this.picture?.label.tr ?? null,
      fails: this.save.fails,
    };
  }

  private async autoplay(): Promise<void> {
    if (this.auto) return;
    this.auto = true;
    for (let guard = 0; guard < 600 && this.sys.isActive() && !this.ended; guard++) {
      while (this.busy && !this.ended) await this.wait(40);
      const a = this.state && this.state.status === "playing" ? chooseAction(this.state, this.botRng, GREEDY) : null;
      if (!a) break;
      await this.perform(a);
    }
    this.auto = false;
  }

  /** Test hook: play the least useful legal launches until the tray overflows. */
  private async autolose(): Promise<void> {
    for (let guard = 0; guard < 200 && !this.ended; guard++) {
      while (this.busy && !this.ended) await this.wait(40);
      if (this.ended || this.state.status !== "playing") break;
      let best: Action | null = null;
      let bestShots = Infinity;
      for (let i = 0; i < this.state.queues.length; i++) {
        const e = evalLaunch(this.state, "queue", i);
        if (e.legal && e.shots < bestShots) {
          bestShots = e.shots;
          best = { t: "q", i };
        }
      }
      if (!best) break;
      await this.perform(best);
    }
  }
}
